import { Text, TextInput, TextArea, Section, Grid, Column } from '@acast/decibel';
import { resolve } from 'acast-www-utils';
import config from 'app/config';
import updateEntity from 'app/entity/actions/update';
import { Episode, Show, UserProfile } from 'client/resources';
import React from 'react';
import { RSS_SUBTITLE_LIMIT } from 'resources/show/schema';
import { getUserId } from 'services/authentication';
import DescriptionAttachable from 'shared/components/description-attachable';
import createRenderToggleDisabled from 'shared/components/description-attachable/create-render-toggle-disabled';
import Field from 'shared/components/field';
import TokenInput from 'shared/components/token-input';
import convertToRssSubtitle from 'shared/helpers/convert-to-rss-subtitle';
import removeHtmlTags from 'shared/helpers/remove-html-tags';
import resourceProp from 'shared/helpers/resource-prop';

const RSSSettings = props => {
  const { episode, show, profile } = props;
  const playHost = config.get('PLAY_HOST', 'https://play.acast.com');
  let hasRssCategories = false;
  let categoriesField = null;

  if (!profile.pending && profile.data) {
    hasRssCategories = profile.data.hasFeature('EPISODE_SETTINGS_RSS_CATEGORIES');
  }

  function setEpisodeProperty(key) {
    return value => {
      const model = episode.data;
      if (model[key] !== value) {
        updateEntity(Episode, model.merge({ [key]: value }));
      }
    };
  }

  function getEpisodeProperty(key, defaultValue) {
    return resolve(episode, `data.${key}`, defaultValue) || defaultValue;
  }

  function getDefaultRssLink() {
    const [showLink] = resolve(show, `data.permalinks`, []);
    const [episodeLink] = resolve(episode, `data.permalinks`, []);

    return showLink && episodeLink ? `${playHost}/s/${showLink}/${episodeLink}` : '';
  }

  function setRssSubtitle(value) {
    setEpisodeProperty('rssSubtitle')(convertToRssSubtitle(value));
  }

  function setRssSummary(value) {
    setEpisodeProperty('rssSummary')(removeHtmlTags(value));
  }

  function setRssLink({ target: { value } }) {
    setEpisodeProperty('rssLink')(removeHtmlTags(value));
  }

  const rssSubtitleValue = getEpisodeProperty('rssSubtitle', '');
  const subtitleField = (
    <DescriptionAttachable
      value={rssSubtitleValue}
      description={convertToRssSubtitle(getEpisodeProperty('description', ''))}
      setValue={setRssSubtitle}
    >
      {({ isAttachedToDescription, attachToDescription, detachFromDescription }) => (
        <Field
          name="rssSubtitle"
          label="Subtitle"
          help={`This is automatically generated from the episode description above and can then be edited. This is shown in the 'description' column in Apple Podcasts and is limited to ${RSS_SUBTITLE_LIMIT} characters.`}
          subtitle={`${RSS_SUBTITLE_LIMIT - rssSubtitleValue.length} characters left`}
          disabled={isAttachedToDescription}
          renderToggleDisabled={createRenderToggleDisabled(
            attachToDescription,
            detachFromDescription
          )}
          isRequired
        >
          <TextArea
            value={rssSubtitleValue}
            onChange={event => setRssSubtitle(event.target.value)}
            maxlength={RSS_SUBTITLE_LIMIT}
          />
        </Field>
      )}
    </DescriptionAttachable>
  );

  const rssSummaryValue = getEpisodeProperty('rssSummary', '');
  const summaryField = (
    <DescriptionAttachable
      value={rssSummaryValue}
      description={removeHtmlTags(getEpisodeProperty('description', ''))}
      setValue={setRssSummary}
    >
      {({ isAttachedToDescription, attachToDescription, detachFromDescription }) => (
        <Field
          name="rssSummary"
          label="Summary"
          help="This is automatically generated from the episode description above and can then be edited. This is the text that appears when clicking the 'i' information symbol next to an episode in Apple Podcasts."
          disabled={isAttachedToDescription}
          renderToggleDisabled={createRenderToggleDisabled(
            attachToDescription,
            detachFromDescription
          )}
          isRequired
        >
          <TextArea value={rssSummaryValue} onChange={event => setRssSummary(event.target.value)} />
        </Field>
      )}
    </DescriptionAttachable>
  );

  const rssLinkValue = getEpisodeProperty('rssLink', '');
  const linkRow = (
    <Field name="link" label="Link">
      <TextInput
        value={rssLinkValue}
        placeholder={getDefaultRssLink()}
        name="link"
        onChange={setRssLink}
      />
    </Field>
  );

  if (hasRssCategories) {
    categoriesField = (
      <Field label="Categories">
        <TokenInput
          tokens={getEpisodeProperty('categories', [])}
          placeholder=""
          onChange={setEpisodeProperty('categories')}
        />
      </Field>
    );
  }

  return (
    <Section>
      <Text variant="h3">RSS Settings</Text>
      <Grid>
        <Column>{subtitleField}</Column>
        <Column>{summaryField}</Column>
        {categoriesField && <Column>{categoriesField}</Column>}
        <Column>{linkRow}</Column>
      </Grid>
    </Section>
  );
};

RSSSettings.propTypes = {
  episode: resourceProp(Episode),
  show: resourceProp(Show),
  profile: resourceProp(UserProfile),
};

const withUserProfile = UserProfile.connect('profile', getUserId);

export default withUserProfile(RSSSettings);
